import { useMutation } from "@apollo/client";
import { useEffect, useMemo, useRef } from "react";
import { shallowEqual } from "react-redux";
import { selectAllergens, selectAppState, selectOpenedDetailDishTypeId } from "slices/appStateSlice";
import { selectAvailableDishTypesIds, selectFilteredDishTypeIds } from "slices/dishesSlice";
import { selectFridgeLightingMode, setLightingMode } from "slices/fridgeLightingSlice";
import { selectActiveLocalTransaction, selectRefill } from "slices/transactionSlice";
import { LightingModeType, LightingSegmentModeType, TransactionMode, TransactionState } from "types";
import { getTabletType, TabletTypeEnum } from "utils/browser/localStorage";
import { debugTrace, shouldTraceLightingDebug } from "utils/debug/debugTrace";
import { SWITCH_FRIDGE_MODE_MUTATION, TOGGLE_SPOTLIGHT_MODE_MUTATION } from "utils/gqlOperations";
import { computeDesiredLighting } from "utils/lightingConvergence";
import { createDefaultLight, createSpotlightLight } from "utils/lightingUtils";
import { setShallowEqual } from "utils/setShallowEqual";
import { useFridgeInformation } from "./useFridgeInformation";
import { useAppDispatch, useAppSelector } from "./utilsHooks/storeHooks";

// Edge-triggered lighting driver. The FE computes a desired lighting target from its OWN state and
// fires a mutation on each change of intent. It does NOT reconcile against the BE's echo to drive that
// decision — reacting to echoes is what let a second writer (a stray second master tablet, or the
// BE/hardware) ping-pong the fridge STAND_BY↔ACTIVE, so ignoring them makes that war impossible by
// construction. A dropped mutation is re-delivered by the Apollo RetryLink.
//
// The two mode directions are handled asymmetrically:
//  - ACTIVE (wake): RECONVERGES — whenever we want the fridge Active and it isn't (boot, or the backend
//    dimmed it while the customer is still here), the FE drives it back to ACTIVE. Pushing only toward
//    ACTIVE — never toward StandBy — cannot reignite the war: the backend accepts ACTIVE and resets its
//    idle timer, so it does not re-dim. (Without this, a backend mid-activity dim left the fridge stuck
//    StandBy with the spotlight gated off.)
//  - STAND_BY (dim): the backend owns idle-dimming, but does it UNRELIABLY — it does not always send
//    StandBy. So on the idle EDGE the FE dims as an ESCAPE HATCH: once, and only if the fridge is not
//    already StandBy. Edge-triggered, so a backend bounce back to ACTIVE is NOT re-fought — no command
//    war. The dim is a SINGLE command: an earlier variant also fired an all-DEFAULT spotlight clear on
//    the same edge, but the two concurrent requests race — the BE processed the StandBy first and the
//    trailing per-dish payload WOKE the fridge, which then stayed bright forever (one-shot hatch; HW
//    capture 2026-06-11). A lingering spotlight is instead cleared on the NEXT wake: the detail closes
//    at the idle edge, so the stale spotlight ref mismatches the [] target and the steady-state block
//    sends the all-DEFAULT cleanup once the fridge is believed Active again. Until that wake the BE may
//    keep the spotlit segment lit through StandBy — accepted trade-off (verify on manual case A6).
//
// `actualMode` is read only to push toward ACTIVE / guard the one-shot StandBy / skip per-dish spotlight
// payloads while StandBy (they would wake the fridge). It never drives a StandBy reconvergence loop.
export const useConvergeLighting = () => {
	const { id: fridgeId } = useFridgeInformation();
	const dispatch = useAppDispatch();
	// In-flight guard for the ACTIVE wake: true while an ACTIVE command awaits its echo, so a busy effect
	// (e.g. dish-set churn) does not spam ACTIVE. Cleared when the BE confirms Active (ack or echo), when
	// the command fails, or when a fresh StandBy belief shows the wake did not stick (see the effect).
	const sentActiveRef = useRef(false);
	const [setMode] = useMutation(SWITCH_FRIDGE_MODE_MUTATION, {
		onError: (error) => {
			// The command was lost (past RetryLink) or rejected — un-latch the in-flight guard so the
			// next effect run can re-send the wake instead of de-duping against a send that never landed.
			sentActiveRef.current = false;
			if (shouldTraceLightingDebug()) {
				debugTrace(`[state-debug] SWITCH-ERR ${error.message}`);
			}
		},
		onCompleted: (data) => {
			// The ack carries the BE-confirmed mode. Feed it into the belief slice: the subscription does
			// not reliably echo mutation-induced mode changes, and a stale belief breaks the guards that
			// read it — a stale StandBy suppressed the idle escape-hatch dim (the fridge never dimmed) and
			// kept the spotlight channel gated off (dishes could not be lit). HW manual cases A2/B1.
			const ackedMode = data?.updateFridge?.lighting?.mode;
			if (ackedMode != null) {
				dispatch(setLightingMode(ackedMode));
			}
			if (shouldTraceLightingDebug()) {
				debugTrace(`[state-debug] SWITCH-OK mode=${ackedMode ?? "?"}`);
			}
		},
	});
	const [setSpotlight] = useMutation(TOGGLE_SPOTLIGHT_MODE_MUTATION, {
		onError: (error) => {
			if (shouldTraceLightingDebug()) {
				debugTrace(`[state-debug] TOGGLE-ERR ${error.message}`);
			}
		},
		onCompleted: (data) => {
			if (shouldTraceLightingDebug()) {
				const types = (data?.updateFridge?.dishes?.types ?? []) as Array<{ lighting?: { mode?: string } }>;
				const ackSpot = types.filter(
					(dishType) => dishType?.lighting?.mode === LightingSegmentModeType.Spotlight,
				).length;
				debugTrace(`[state-debug] TOGGLE-OK ackSpot=${ackSpot}`);
			}
		},
	});

	const appState = useAppSelector(selectAppState);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const refill = useAppSelector(selectRefill);
	const openDetailDishTypeId = useAppSelector(selectOpenedDetailDishTypeId);
	const allergens = useAppSelector(selectAllergens);
	const filteredDishTypeIds = useAppSelector(selectFilteredDishTypeIds);
	const availableDishTypeIds = useAppSelector(selectAvailableDishTypesIds);
	// Read only as a one-shot guard (see the mode/spotlight blocks): suppress a redundant escape-hatch
	// StandBy when already dimmed, and suppress spotlight payloads while StandBy. Never a reconvergence loop.
	const actualMode = useAppSelector(selectFridgeLightingMode);

	// Lighting-wise a RUNNING remote refill is a transaction like any other — the stocker is physically
	// at the fridge, so it wakes and holds ACTIVE. It is excluded from the local ORDER flow
	// (selectActiveLocalTransaction), not from lighting. Its END counts as fresh presence: DeviceContext
	// re-arms the idle timer and sets appState ACTIVE (mirroring finishOrder), so the fridge dims a full
	// inactive timeout later via the normal idle escape hatch — without this, the BE-lit fridge stayed
	// ACTIVE forever after a remote refill (HW capture 11. 6. 2026).
	const activeRemoteRefill =
		refill != null && refill.mode === TransactionMode.Remote && refill.state !== TransactionState.Finished
			? refill
			: null;
	const lightingTransaction = transaction ?? activeRemoteRefill;
	const hasTransaction = lightingTransaction != null;
	const transactionState = lightingTransaction?.state ?? null;

	const desired = useMemo(
		() =>
			computeDesiredLighting({
				appState: appState,
				hasTransaction: hasTransaction,
				transactionState: transactionState,
				openDetailDishTypeId: openDetailDishTypeId,
				allergens: allergens,
				filteredDishTypeIds: filteredDishTypeIds,
			}),
		[appState, hasTransaction, transactionState, openDetailDishTypeId, allergens, filteredDishTypeIds],
	);

	// Previous desired mode — to detect the EDGE into StandBy for the one-shot escape-hatch dim.
	const prevDesiredModeRef = useRef<LightingModeType | null>(null);
	// Previous believed mode — to detect a fresh transition INTO StandBy, which re-arms the wake.
	const prevActualModeRef = useRef<LightingModeType | null>(null);
	const lastCommandedSpotlightRef = useRef<readonly string[] | null>(null);
	// Identity of the available-dish set at the last spotlight send. A change here re-fires the
	// spotlight even with an unchanged target, so a newly-available dish still receives its DEFAULT
	// entry (the payload is built from this set).
	const lastCommandedAvailableRef = useRef<ReadonlySet<string> | null>(null);

	// Steady-state driver. Drives the fridge toward `desired`; the two mode directions are asymmetric.
	useEffect(() => {
		if (getTabletType() !== TabletTypeEnum.Master || fridgeId == null) {
			return;
		}

		const enteredStandBy =
			desired.mode === LightingModeType.StandBy && prevDesiredModeRef.current !== LightingModeType.StandBy;
		prevDesiredModeRef.current = desired.mode;

		// A fresh belief transition INTO StandBy while a wake is in flight means the wake did not stick
		// (the BE dimmed over it, or the Active ack/echo was lost) — re-arm so ACTIVE can be re-asserted.
		// Edge-triggered on the BELIEF, so an unchanged StandBy cannot loop; and it only ever enables a
		// push toward ACTIVE, which the BE accepts (no command war). HW manual case B1: after a backend
		// self-dim the spotlights stayed dead because the latched guard blocked every re-wake.
		const beliefEnteredStandBy =
			actualMode === LightingModeType.StandBy && prevActualModeRef.current !== LightingModeType.StandBy;
		prevActualModeRef.current = actualMode;
		if (beliefEnteredStandBy) {
			sentActiveRef.current = false;
		}

		if (desired.mode === LightingModeType.Active) {
			// WAKE — reconverge toward ACTIVE: whenever we want the fridge Active and it isn't (boot, or the
			// backend dimmed it on its own timer while the customer is still here), drive it back. Reading
			// actualMode here only ever pushes toward ACTIVE — never toward StandBy — so it cannot reignite
			// the command war: the backend accepts ACTIVE and resets its idle timer, so it does not re-dim.
			// sentActiveRef de-dupes while the ACTIVE echo is in flight.
			if (actualMode === LightingModeType.Active) {
				sentActiveRef.current = false;
			} else if (!sentActiveRef.current) {
				sentActiveRef.current = true;
				if (shouldTraceLightingDebug()) {
					debugTrace(`[state-debug] FIRE-SWITCH→ACTIVE (actual=${actualMode})`);
				}
				setMode({ variables: { input: { id: fridgeId, lighting: { mode: LightingModeType.Active } } } });
			}
		} else {
			sentActiveRef.current = false;
			// ESCAPE HATCH — dim once on the edge into StandBy, only if the fridge isn't already StandBy.
			// The backend owns idle-dimming but does it unreliably (doesn't always send StandBy). One-shot
			// (edge-triggered), so a backend bounce back to ACTIVE is not re-fought. The dim is a SINGLE
			// SWITCH — no per-dish payload may accompany or follow it: the BE can process the StandBy first,
			// and a per-dish payload landing on a StandBy fridge wakes it back up (HW capture 2026-06-11:
			// the old clear-on-dim TOGGLE lost that race and the fridge stayed bright forever). The lingering
			// spotlight ref is intentionally NOT touched here — the next wake clears it (spotlight block below).
			if (enteredStandBy && actualMode !== LightingModeType.StandBy) {
				if (shouldTraceLightingDebug()) {
					debugTrace(`[state-debug] FIRE-SWITCH→STAND_BY (escape-hatch, actual=${actualMode})`);
				}
				setMode({ variables: { input: { id: fridgeId, lighting: { mode: LightingModeType.StandBy } } } });
			}
		}

		// SPOTLIGHT edge — per-dish state is only meaningful in Active (a per-dish payload would wake the
		// fridge out of StandBy), and only when there are dishes to address. We KEEP the spotlight ref
		// across a StandBy dip so returning to Active with the same target does not re-fire. Keeping it is
		// also what CLEARS a spotlight left set at the idle dim: the detail auto-closes at that edge, so
		// the re-wake's [] target mismatches the stale ref and the all-DEFAULT cleanup fires here — after
		// the ACTIVE ack opens the gate below.
		//
		// Skip entirely while the fridge is actually StandBy (`actualMode === StandBy`): the BE has dimmed
		// and a per-dish payload would wake it back up. This is what makes closing the detail on a BE dim
		// (see MealList) safe — the resulting spotlight clear is suppressed instead of un-dimming the fridge.
		if (
			desired.mode !== LightingModeType.Active ||
			availableDishTypeIds.size === 0 ||
			actualMode === LightingModeType.StandBy
		) {
			return;
		}

		const spotlightChanged =
			lastCommandedSpotlightRef.current == null ||
			!shallowEqual(lastCommandedSpotlightRef.current, desired.spotlightDishTypeIds);
		const availableChanged =
			lastCommandedAvailableRef.current == null ||
			!setShallowEqual(lastCommandedAvailableRef.current, availableDishTypeIds);

		if (!(spotlightChanged || availableChanged)) {
			return;
		}

		lastCommandedSpotlightRef.current = desired.spotlightDishTypeIds;
		lastCommandedAvailableRef.current = availableDishTypeIds;

		const desiredSet = new Set(desired.spotlightDishTypeIds);
		const types = Array.from(availableDishTypeIds).map((id) =>
			desiredSet.has(id) ? createSpotlightLight(id) : createDefaultLight(id),
		);
		if (shouldTraceLightingDebug()) {
			// payloadSpot = how many desired targets actually became SPOTLIGHT entries in the payload (a
			// target only counts if it is in availableDishTypeIds). missing = targets dropped because they
			// are NOT available — the prime suspect for a dish that stays dark.
			const target = desired.spotlightDishTypeIds;
			const payloadSpot = target.filter((id) => availableDishTypeIds.has(id)).length;
			const missing = target.filter((id) => !availableDishTypeIds.has(id));
			debugTrace(
				`[state-debug] FIRE-TOGGLE target=[${target.join(",")}] avail=${availableDishTypeIds.size} payloadSpot=${payloadSpot} missing=[${missing.join(",")}]`,
			);
		}
		setSpotlight({ variables: { input: { id: fridgeId, dishes: { types: types } } } });
	}, [desired, availableDishTypeIds, actualMode, fridgeId, setMode, setSpotlight]);
};
